/**
 * `events` — the museum's own afisha.
 *
 * `actions` (§11.4/§11.5) is the CARD's own footer — «Афиша на другую дату» —
 * through the shared `CardActions`; each item's own «ℹ️ Подробнее» button is
 * unchanged, a per-item field the catalogue has carried since before this wave.
 * `highlight` (§11.6) picks items out by their `key`, never filters `items`.
 */

import type { EventsListProps } from '../generated/schemas';
import { send } from '../../lib/send';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import { useSendLocked } from '../../chat/turnLock';
import CardActions from './CardActions';
import CollapsibleRows from './CollapsibleRows';

type EventItem = EventsListProps['items'][number];

export default function EventsList({ props }: { props: EventsListProps }) {
  const sendLocked = useSendLocked();
  return (
    <article className="card card--events">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'Афиша')}</h3>
      </header>
      <ul className="rows">
        <CollapsibleRows<EventItem>
          rows={props.items}
          highlight={props.highlight}
          keyOf={(item) => item.key}
          renderRow={(item, { highlighted, revealed }) => (
            <li
              key={`${item.title}:${item.date_label ?? ''}`}
              className={cn('rows__item', highlighted && 'rows__item--highlight', revealed && 'row-reveal')}
            >
              {item.image ? (
                <img
                  className="rows__thumb"
                  src={item.image}
                  alt=""
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <div className="rows__body">
                {item.date_label ? <span className="rows__date">{plain(item.date_label)}</span> : null}
                <span className="rows__title">{plain(item.title)}</span>
                {item.venue ? <span className="rows__sub">{plain(item.venue)}</span> : null}
              </div>
              {item.url ? (
                <a
                  className="chip chip--link"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Билеты
                </a>
              ) : item.value ? (
                <button type="button" className="chip" disabled={sendLocked} onClick={() => send(item.value!)}>
                  Подробнее
                </button>
              ) : null}
            </li>
          )}
          renderToggle={(button) => <li className="rows__disclosure">{button}</li>}
        />
      </ul>
      <CardActions sourceId={props.id} actions={props.actions} />
    </article>
  );
}
